import { Globe2, Linkedin, Mail, MessageCircle, Send } from "lucide-react";

const contactIconMap = {
  email: Mail,
  whatsapp: MessageCircle,
  linkedin: Linkedin,
  telegram: Send,
  vk: Send,
};

const contactLabels = {
  email: "Email",
  whatsapp: "WhatsApp",
  linkedin: "LinkedIn",
  telegram: "Telegram",
  vk: "VK",
};

const getContactText = (type, value) => (type === "email" ? value : contactLabels[type] || type);

const hasProtocol = (value) => /^https?:\/\//i.test(value);
const withHttps = (value) => (hasProtocol(value) ? value : `https://${value.replace(/^\/+/, "")}`);
const isDomainLike = (value) => /(^|\.)[a-z0-9-]+\.[a-z]{2,}/i.test(value);

const getContactHref = (type, value) => {
  if (!value) return "";
  if (type === "email") return `mailto:${value}`;
  if (hasProtocol(value)) return value;
  if (type === "whatsapp") {
    if (isDomainLike(value)) return withHttps(value);
    const digits = value.replace(/[^0-9]/g, "");
    return digits ? `https://wa.me/${digits}` : "";
  }
  if (type === "telegram") {
    if (isDomainLike(value)) return withHttps(value);
    return `https://t.me/${value.replace(/^@/, "")}`;
  }
  if (type === "vk") {
    if (isDomainLike(value)) return withHttps(value);
    return `https://vk.com/${value.replace(/^@/, "")}`;
  }
  if (type === "linkedin") {
    if (isDomainLike(value)) return withHttps(value);
    return `https://www.linkedin.com/in/${value.replace(/^@/, "")}`;
  }
  return value;
};

export function Team({ content, images = [] }) {
  return (
    <section className="section" data-slide="08" data-testid="team-section">
      <div className="section-heading wide">
        <p className="overline" data-testid="team-overline">{content.overline}</p>
        <h2 data-testid="team-title">{content.title}</h2>
      </div>

      <div className="team-grid" data-testid="team-grid">
        {content.teams.map((team, index) => {
          const [region, representative, coverage = []] = Array.isArray(team) ? team : [team.region, team.representative, team.coverage || []];
          const representativeName = String(images[index]?.name || "").trim();
          const contacts = Array.isArray(team) ? team[3] || {} : team.contacts || {};
          const visibleContacts = Object.entries(contacts).filter(([type, value]) => getContactHref(type, String(value || "").trim()));
          return (
          <article className="team-card" key={region} data-testid={`team-card-${index + 1}`}>
            <div className="portrait-placeholder" data-testid={`team-photo-placeholder-${index + 1}`}>
              {images[index]?.image ? (
                <img src={images[index].image} alt={`${representative} - ${region}`} data-testid={`team-photo-${index + 1}`} />
              ) : (
                <>
                  <Globe2 size={34} aria-hidden="true" />
                  <span>{content.photo}</span>
                </>
              )}
            </div>
            <div className="team-card-body">
              <h3 data-testid={`team-region-${index + 1}`}>{region}</h3>
              {representativeName && <strong className="team-person-name" data-testid={`team-person-name-${index + 1}`}>{representativeName}</strong>}
              <p className="team-role" data-testid={`team-representative-${index + 1}`}>{representative}</p>
              <ul data-testid={`team-coverage-list-${index + 1}`}>
                {coverage.map((coverageItem) => (
                  <li key={coverageItem} data-testid={`team-coverage-${index + 1}-${coverageItem.toLowerCase().replaceAll(" ", "-")}`}>{coverageItem}</li>
                ))}
              </ul>
              {visibleContacts.length > 0 && (
                <div className="team-contact-links" data-testid={`team-contact-links-${index + 1}`}>
                  {visibleContacts.map(([type, value]) => {
                    const Icon = contactIconMap[type] || Send;
                    const trimmedValue = String(value).trim();
                    const label = contactLabels[type] || type;
                    const href = getContactHref(type, trimmedValue);
                    const text = getContactText(type, trimmedValue);
                    return (
                      <a key={type} href={href} target={type === "email" ? undefined : "_blank"} rel="noreferrer" aria-label={`${representative} ${label}`} data-testid={`team-contact-${index + 1}-${type}-link`}>
                        <Icon size={16} aria-hidden="true" />
                        <span>{text}</span>
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          </article>
          );
        })}
      </div>
    </section>
  );
}